import { Box, Paper, Tooltip, Typography } from "@mui/material";

import BotsRecordTable, { type BotsColumn } from "components/botsManagement/BotsRecordTable";
import type { PdfJobDetail, PdfJobFileSummary } from "features/pdfTools/pdfTools";

function StatTile({
  label,
  value,
  tone = "default",
  tooltip,
}: {
  label: string;
  value: number;
  tone?: "default" | "warning";
  tooltip?: string;
}) {
  const tile = (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5,
        py: 1,
        minWidth: 0,
        borderRadius: 2,
        ...(tone === "warning" && value > 0
          ? { bgcolor: "rgba(237,108,2,0.08)", borderColor: "warning.light" }
          : {}),
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {value.toLocaleString()}
      </Typography>
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip}>{tile}</Tooltip> : tile;
}

const numberCell = (value: number) => (
  <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
    {value.toLocaleString()}
  </Typography>
);

const FILE_COLUMNS: BotsColumn<PdfJobFileSummary>[] = [
  {
    key: "file",
    header: "Input file",
    align: "left",
    card: "title",
    cell: (row) => (
      <Typography variant="body2" noWrap title={row.file_name} sx={{ fontWeight: 600 }}>
        {row.file_name}
      </Typography>
    ),
    cardCell: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
        {row.file_name}
      </Typography>
    ),
  },
  { key: "pages", header: "Pages", width: 96, cell: (row) => numberCell(row.pages) },
  { key: "renamed", header: "Files", width: 96, cell: (row) => numberCell(row.renamed) },
  {
    key: "errors",
    header: "Error pages",
    width: 112,
    cell: (row) => numberCell(row.error_pages),
  },
  { key: "skipped", header: "Skipped", width: 96, cell: (row) => numberCell(row.skipped) },
  {
    key: "note",
    header: "Note",
    width: 200,
    compactWidth: 160,
    align: "left",
    cell: (row) =>
      row.aborted ? (
        <Typography
          variant="caption"
          color="error"
          noWrap
          title={row.abort_reason ?? undefined}
          sx={{ display: "block" }}
        >
          Aborted{row.abort_reason ? ` (${row.abort_reason})` : ""}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      ),
  },
];

/** What a finished split produced, and per input file. */
export default function JobSummaryPanel({ job }: { job: PdfJobDetail }) {
  const totals = job.summary?.totals ?? {};
  const perFile = job.summary?.per_file ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 1.25,
        }}
      >
        <StatTile label="Pages processed" value={totals.pages ?? 0} />
        <StatTile label="Employee folders" value={totals.employee_folders ?? 0} />
        <StatTile label="Files created" value={totals.renamed ?? 0} />
        <StatTile
          label="Error pages"
          value={totals.error_pages ?? 0}
          tone="warning"
          tooltip="Pages where the employee name, SSN or year could not be read. Review them in the Workspace tab."
        />
        <StatTile
          label="Skipped pages"
          value={totals.skipped ?? 0}
          tooltip="Pages of another document type (instruction pages, for one) that were not processed."
        />
      </Box>

      {perFile.length > 0 && (
        <BotsRecordTable
          rows={perFile}
          columns={FILE_COLUMNS}
          rowKey={(row) => row.file_name}
          stickyHeader={false}
        />
      )}
    </Box>
  );
}
